<!-- eslint-disable vue/no-bare-strings-in-template, @intlify/vue-i18n/no-raw-text -->
<script setup>
/* eslint-disable vue/no-bare-strings-in-template, @intlify/vue-i18n/no-raw-text */
import { computed, onMounted, ref } from 'vue';
import { useAlert } from 'dashboard/composables';
import Button from 'dashboard/components-next/button/Button.vue';
import Dialog from 'dashboard/components-next/dialog/Dialog.vue';
import TalkviaPlanAdjustmentsAPI from 'dashboard/api/talkviaPlanAdjustments';

const props = defineProps({
  subscriptionValue: { type: Number, default: 0 },
  nextDueDate: { type: String, default: '' },
});

const ICONS = {
  additional_user: 'i-hugeicons-user-add-01',
  additional_inbox: 'i-hugeicons-inbox',
};
const STATUS = {
  scheduled: { label: 'Agendada', class: 'bg-n-amber-3 text-n-amber-11' },
  applied: { label: 'Aplicada', class: 'bg-n-teal-3 text-n-teal-11' },
  canceled: { label: 'Cancelada', class: 'bg-n-alpha-2 text-n-slate-11' },
};

const summary = ref({ items: [], adjustments: [] });
const isLoading = ref(true);
const isSaving = ref(false);
const loadFailed = ref(false);
const selectedItem = ref(null);
const quantity = ref(1);
const reduceDialog = ref(null);
const cancelDialog = ref(null);
const adjustmentToCancel = ref(null);

const currency = value =>
  new Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'BRL' }).format(
    Number(value || 0)
  );
const formatDate = value => {
  if (!value) return '';
  const [year, month, day] = String(value).slice(0, 10).split('-');
  return `${day}/${month}/${year}`;
};

const visibleItems = computed(() =>
  summary.value.items.filter(
    item => item.active_quantity > 0 || item.scheduled_reduction > 0
  )
);
const recentAdjustments = computed(() => summary.value.adjustments.slice(0, 5));

const reductionAmount = computed(() => {
  const prices = selectedItem.value?.reduction_unit_prices || [];
  return prices
    .slice(0, quantity.value)
    .reduce((total, price) => total + Number(price), 0);
});
const newMonthlyValue = computed(() =>
  Math.max(Number(props.subscriptionValue || 0) - reductionAmount.value, 0)
);
const newLimit = computed(() =>
  selectedItem.value
    ? selectedItem.value.limit -
      selectedItem.value.scheduled_reduction -
      quantity.value
    : 0
);

const errorMessage = error =>
  error?.response?.data?.error ||
  'Não foi possível concluir o ajuste agora. Tente novamente em instantes.';

const load = async () => {
  isLoading.value = true;
  try {
    const { data } = await TalkviaPlanAdjustmentsAPI.get();
    summary.value = data;
    loadFailed.value = false;
  } catch {
    loadFailed.value = true;
  } finally {
    isLoading.value = false;
  }
};

const openReduce = item => {
  selectedItem.value = item;
  quantity.value = 1;
  reduceDialog.value?.open();
};

const changeQuantity = step => {
  const max = selectedItem.value?.reducible_quantity || 1;
  quantity.value = Math.min(Math.max(quantity.value + step, 1), max);
};

const confirmReduce = async () => {
  if (!selectedItem.value) return;
  isSaving.value = true;
  try {
    const { data } = await TalkviaPlanAdjustmentsAPI.scheduleReduction(
      selectedItem.value.key,
      quantity.value
    );
    summary.value = data;
    reduceDialog.value?.close();
    useAlert(
      `Redução agendada. A nova mensalidade vale a partir de ${formatDate(data.adjustment?.effective_on)}.`
    );
  } catch (error) {
    useAlert(errorMessage(error));
  } finally {
    isSaving.value = false;
  }
};

const openCancel = adjustment => {
  adjustmentToCancel.value = adjustment;
  cancelDialog.value?.open();
};

const confirmCancel = async () => {
  if (!adjustmentToCancel.value) return;
  isSaving.value = true;
  try {
    const { data } = await TalkviaPlanAdjustmentsAPI.cancel(
      adjustmentToCancel.value.id
    );
    summary.value = data;
    cancelDialog.value?.close();
    useAlert('Redução cancelada. O valor voltou para a assinatura.');
  } catch (error) {
    useAlert(errorMessage(error));
  } finally {
    isSaving.value = false;
  }
};

onMounted(load);
</script>

<template>
  <section
    class="overflow-hidden rounded-2xl border border-n-weak bg-n-solid-2 shadow-sm"
    data-testid="plan-addons"
  >
    <div class="flex items-start gap-3 border-b border-n-weak p-5">
      <span
        class="grid size-10 shrink-0 place-items-center rounded-xl bg-n-brand/10 text-n-brand"
      >
        <span class="i-hugeicons-package size-5" aria-hidden="true" />
      </span>
      <div class="min-w-0">
        <h2 class="mb-1 text-base font-semibold text-n-slate-12">
          Plano e adicionais
        </h2>
        <p class="mb-0 text-sm text-n-slate-10">
          Reduza usuários ou caixas adicionais. A mudança vale a partir do
          próximo vencimento; até lá, tudo continua disponível.
        </p>
      </div>
    </div>

    <p v-if="isLoading" class="mb-0 p-5 text-sm text-n-slate-11">
      Carregando adicionais…
    </p>
    <p v-else-if="loadFailed" class="mb-0 p-5 text-sm text-n-slate-11">
      Não foi possível carregar os adicionais agora.
    </p>
    <p
      v-else-if="!visibleItems.length"
      class="mb-0 p-5 text-sm text-n-slate-11"
      data-testid="plan-addons-empty"
    >
      A empresa não tem usuários ou caixas adicionais contratados.
    </p>

    <div v-else class="grid gap-3 p-5 md:grid-cols-2">
      <div
        v-for="item in visibleItems"
        :key="item.key"
        class="grid gap-3 rounded-xl border border-n-weak bg-n-solid-1 p-4"
        :data-testid="`plan-addon-${item.key}`"
      >
        <div class="flex items-start justify-between gap-3">
          <div class="flex min-w-0 items-center gap-3">
            <span
              class="grid size-9 shrink-0 place-items-center rounded-lg bg-n-alpha-2 text-n-slate-11"
            >
              <span
                :class="ICONS[item.key]"
                class="size-4"
                aria-hidden="true"
              />
            </span>
            <div class="min-w-0">
              <p class="mb-0 text-sm font-medium text-n-slate-12">
                {{ item.label }}
              </p>
              <p class="mb-0 text-xs text-n-slate-11">
                {{
                  `${item.active_quantity} contratado(s) · ${currency(item.monthly_value)}/mês`
                }}
              </p>
            </div>
          </div>
          <Button
            size="sm"
            color="slate"
            variant="faded"
            label="Reduzir"
            :disabled="!item.reducible_quantity"
            :data-testid="`reduce-${item.key}`"
            @click="openReduce(item)"
          />
        </div>
        <div class="flex flex-wrap gap-x-4 gap-y-1 text-xs text-n-slate-11">
          <span>{{ `Limite: ${item.limit}` }}</span>
          <span>{{ `Em uso: ${item.usage}` }}</span>
          <span
            v-if="item.scheduled_reduction"
            class="font-medium text-n-amber-11"
          >
            {{ `Redução agendada: ${item.scheduled_reduction}` }}
          </span>
        </div>
        <p
          v-if="
            !item.reducible_quantity &&
            item.active_quantity > item.scheduled_reduction
          "
          class="mb-0 rounded-lg bg-n-alpha-1 px-3 py-2 text-xs text-n-slate-11"
        >
          {{
            `Todas as vagas estão em uso. Para reduzir, remova antes ${item.usage_label}.`
          }}
        </p>
      </div>
    </div>

    <div
      v-if="recentAdjustments.length"
      class="border-t border-n-weak px-5 py-4"
    >
      <h3 class="mb-2 text-sm font-semibold text-n-slate-12">
        Ajustes recentes
      </h3>
      <ul class="m-0 grid list-none gap-2 p-0">
        <li
          v-for="adjustment in recentAdjustments"
          :key="adjustment.id"
          class="flex flex-wrap items-center gap-x-3 gap-y-1 text-sm"
          :data-testid="`adjustment-${adjustment.id}`"
        >
          <span
            class="rounded-full px-2 py-0.5 text-xs font-medium"
            :class="STATUS[adjustment.status]?.class"
          >
            {{ STATUS[adjustment.status]?.label }}
          </span>
          <span class="text-n-slate-12">
            {{ `-${adjustment.quantity} ${adjustment.label}` }}
          </span>
          <span class="text-xs text-n-slate-11">
            {{
              `${currency(adjustment.monthly_amount)}/mês · a partir de ${formatDate(adjustment.effective_on)}`
            }}
          </span>
          <Button
            v-if="adjustment.cancelable"
            size="xs"
            color="slate"
            variant="ghost"
            label="Cancelar redução"
            class="ml-auto"
            @click="openCancel(adjustment)"
          />
        </li>
      </ul>
    </div>

    <Dialog
      ref="reduceDialog"
      :title="`Reduzir ${selectedItem?.label?.toLowerCase() || 'adicional'}`"
      description="A empresa continua usando as vagas atuais até o próximo vencimento. Cobranças já emitidas não mudam."
      confirm-button-label="Agendar redução"
      :is-loading="isSaving"
      @confirm="confirmReduce"
    >
      <div v-if="selectedItem" class="grid gap-4">
        <div class="flex items-center justify-between gap-3">
          <span class="text-sm text-n-slate-12">Quantidade a reduzir</span>
          <div class="flex items-center gap-2">
            <Button
              icon="i-hugeicons-minus-sign"
              size="sm"
              color="slate"
              variant="faded"
              :disabled="quantity <= 1"
              aria-label="Diminuir"
              @click="changeQuantity(-1)"
            />
            <span
              class="w-8 text-center text-lg font-semibold tabular-nums text-n-slate-12"
              data-testid="reduce-quantity"
            >
              {{ quantity }}
            </span>
            <Button
              icon="i-hugeicons-plus-sign"
              size="sm"
              color="slate"
              variant="faded"
              :disabled="quantity >= selectedItem.reducible_quantity"
              aria-label="Aumentar"
              @click="changeQuantity(1)"
            />
          </div>
        </div>
        <dl
          class="m-0 grid grid-cols-2 gap-x-4 gap-y-2 rounded-xl border border-n-weak bg-n-alpha-1 p-4 text-sm"
        >
          <dt class="text-n-slate-11">Redução mensal</dt>
          <dd class="m-0 text-right font-medium text-n-slate-12">
            {{ `- ${currency(reductionAmount)}` }}
          </dd>
          <dt class="text-n-slate-11">Nova mensalidade</dt>
          <dd
            class="m-0 text-right font-semibold text-n-slate-12"
            data-testid="new-monthly-value"
          >
            {{ currency(newMonthlyValue) }}
          </dd>
          <dt class="text-n-slate-11">Novo limite</dt>
          <dd class="m-0 text-right font-medium text-n-slate-12">
            {{ newLimit }}
          </dd>
          <dt class="text-n-slate-11">Vale a partir de</dt>
          <dd class="m-0 text-right font-medium text-n-slate-12">
            {{ nextDueDate ? formatDate(nextDueDate) : 'próximo vencimento' }}
          </dd>
        </dl>
      </div>
    </Dialog>

    <Dialog
      ref="cancelDialog"
      type="alert"
      title="Cancelar redução"
      description="O valor volta para a assinatura e o limite atual é mantido."
      confirm-button-label="Cancelar redução"
      cancel-button-label="Manter redução"
      :is-loading="isSaving"
      @confirm="confirmCancel"
    />
  </section>
</template>
